// jobService.js
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  documentId,
  deleteDoc
} from 'firebase/firestore';
// import { , writeBatch, getDoc } from 'firebase/firestore';
import keywordService from './keywordService';
import { db } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import descriptionGenerator from '../AI/descriptionGenerator';
import jobPostParser from '../AI/jobPostParser';
const functions = getFunctions(); 

class JobService {

  constructor() {
    this.jobsCollection = 'jobs'; // Your Firestore collection name
    this.lastVisible = null; // For pagination
  }


  /**
 * Get total count of jobs in database
 * @returns {Promise<number>}
 */
  async getTotalJobCount() {
  try {
    const snapshot = await getDoc(doc(db, 'metadata', 'jobCount'));
    if (snapshot.exists()) {
      return snapshot.data().count;
    }
    // Fallback: full scan if counter doc doesn't exist yet
    const fullSnapshot = await getDocs(collection(db, this.jobsCollection));
    return fullSnapshot.size;
  } catch (error) {
    console.error('Error getting total job count:', error);
    return 0;
  }
}

  /**
   * Fetch initial jobs with pagination
   * @param {number} pageSize - Number of jobs to fetch
   * @param {object} filters - Optional filters {search, company, salaryMin, salaryMax}
   * @returns {Promise<{jobs: Array, lastDoc: any, hasMore: boolean}>}
   */
  async getJobs(pageSize = 20, filters = {}) {
    try {
        let jobsQuery = query(
            collection(db, this.jobsCollection),
            orderBy('postedDate', 'desc'),
            limit(pageSize)
        );

        // if (filters.company) {
        //   jobsQuery = query(
        //       jobsQuery,
        //       where('company', '==', filters.company)
        //   );
        // }

        const querySnapshot = await getDocs(jobsQuery);
        
        const jobs = [];
        querySnapshot.forEach((doc) => {
            jobs.push({
                id: doc.id,
                ...doc.data()
            });
        });

        // Process jobs to generate missing descriptions
        const processedJobs = await this.processJobDescriptions(jobs);

        const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        this.lastVisible = lastDoc;

        const hasMore = querySnapshot.docs.length === pageSize;

        return {
            jobs: processedJobs,  // Return processed jobs instead
            lastDoc,
            hasMore
        };
    } catch (error) {
        console.error('Error fetching jobs:', error);
        throw error;
    }
}

  /**
   * Fetch next page of jobs
   * @param {number} pageSize - Number of jobs to fetch
   * @param {any} lastDoc - Last document from previous query
   * @param {object} filters - Optional filters
   * @returns {Promise<{jobs: Array, lastDoc: any, hasMore: boolean}>}
   */
  async getNextJobs(pageSize = 20, lastDoc = null, filters = {}) {
    try {
        if (!lastDoc) {
        return this.getJobs(pageSize, filters);
        }

        let jobsQuery = query(
            collection(db, this.jobsCollection),
            orderBy('postedDate', 'desc'),
            startAfter(lastDoc),
            limit(pageSize)
        );

        if (filters.company) {
        jobsQuery = query(
            jobsQuery,
            where('company', '==', filters.company)
        );
        }

        const querySnapshot = await getDocs(jobsQuery);
        
        const jobs = [];
        querySnapshot.forEach((doc) => {
            jobs.push({
                id: doc.id,
                ...doc.data()
            });
        });

        // Process jobs to generate missing descriptions
        const processedJobs = await this.processJobDescriptions(jobs);

        const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        this.lastVisible = newLastDoc;

        const hasMore = querySnapshot.docs.length === pageSize;

        return {
            jobs: processedJobs,  // Return processed jobs instead
            lastDoc: newLastDoc,
            hasMore
        };
    } catch (error) {
        console.error('Error fetching next jobs:', error);
        throw error;
    }
}


  /**
   * Get a single job by ID
   * @param {string} jobId - Job document ID
   * @returns {Promise<object>}
   */
    async getJobById(jobId) {
        try {
        const jobDoc = await getDoc(doc(db, this.jobsCollection, jobId));
        
        if (jobDoc.exists()) {
            return {
            id: jobDoc.id,
            ...jobDoc.data()
            };
        } else {
            throw new Error('Job not found');
        }
        } catch (error) {
        console.error('Error fetching job:', error);
        throw error;
        }
    }

    /**
     * Get multiple jobs by their IDs
     * @param {Array<string>} jobIds - Array of job document IDs
     * @returns {Promise<Array>}
     */
    async getJobsByIds(jobIds) {
      try {
        if (!jobIds || jobIds.length === 0) {
          return [];
        }

        // Firestore 'in' queries are limited to 10 items at a time
        // So we need to batch them
        const batches = [];
        const batchSize = 10;
        
        for (let i = 0; i < jobIds.length; i += batchSize) {
          const batchIds = jobIds.slice(i, i + batchSize);
          
          const jobsQuery = query(
            collection(db, this.jobsCollection),
            where(documentId(), 'in', batchIds)
          );
          
          batches.push(getDocs(jobsQuery));
        }

        // Execute all batches in parallel
        const snapshots = await Promise.all(batches);
        
        const jobs = [];
        snapshots.forEach(snapshot => {
          snapshot.forEach(doc => {
            jobs.push({
              id: doc.id,
              ...doc.data()
            });
          });
        });

        return jobs;
      } catch (error) {
        console.error('Error fetching jobs by IDs:', error);
        throw error;
      }
    }

    /**
     * Search jobs (client-side filtering)
     * Note: For production, consider using Algolia or Firebase Extensions
     * @param {string} searchTerm - Search term
     * @param {Array} jobs - Array of jobs to search through
     * @returns {Array}
     */
    searchJobs(searchTerm, jobs) {
        if (!searchTerm) return jobs;

        const lowerSearch = searchTerm.toLowerCase();
        
        return jobs.filter(job => 
        job.title?.toLowerCase().includes(lowerSearch) ||
        job.company?.toLowerCase().includes(lowerSearch) ||
        job.description?.toLowerCase().includes(lowerSearch)
        );
    }

    /**
     * Filter jobs by salary range (client-side)
     * @param {Array} jobs - Array of jobs
     * @param {number} minSalary - Minimum salary
     * @param {number} maxSalary - Maximum salary
     * @returns {Array}
     */
    filterBySalary(jobs, minSalary, maxSalary) {
        return jobs.filter(job => {
            // Assuming salary is stored as a string like "$15 - 25/hr"
            // Extract the minimum salary value
            const salaryMatch = job.salary?.match(/\$(\d+)/);
            if (!salaryMatch) return true;

            const jobMinSalary = parseInt(salaryMatch[1]);
            
            if (minSalary && jobMinSalary < minSalary) return false;
            if (maxSalary && jobMinSalary > maxSalary) return false;
            
            return true;
        });
    }

    /**
     * Update job description in Firestore
     * @param {string} jobId - Job document ID
     * @param {string} description - The new description
     */
    async updateJobDescription(jobId, description) {
        try {
            const jobRef = doc(db, this.jobsCollection, jobId);
            await updateDoc(jobRef, {
                description: description
            });
            // console.log(`Updated description for job ${jobId}`);
        } catch (error) {
            console.error(`Error updating job ${jobId}:`, error);
            throw error;
        }
    }


/**
 * Process jobs: generate descriptions AND parse structure
 * @param {Array} jobs - Array of job objects
 * @returns {Promise<Array>} Jobs with generated descriptions and structured data
 */
async processJobDescriptions(jobs) {
  const processedJobs = [];
  
  for (const job of jobs) {
    let updatedJob = { ...job };

    // 1. Generate description if empty
    if (descriptionGenerator.isDescriptionEmpty(job.description)) {
      // console.log(`Generating description for job: ${job.title} (ID: ${job.id})`);
      
      try {
        if (!job.fullJobPost || job.fullJobPost.trim() === '') {
          console.warn(`Job ${job.id} has no fullJobPost field, skipping description generation`);
        } else {
          const generatedDescription = await descriptionGenerator.generateDescription(
            job.title,
            job.company,
            job.fullJobPost
          );
          
          await this.updateJobDescription(job.id, generatedDescription);
          updatedJob.description = generatedDescription;
          // console.log(`Successfully saved description for job ${job.id}`);
        }
      } catch (error) {
        console.error(`Failed to generate/save description for job ${job.id}:`, error);
      }
    }

    // 2. Parse job structure if missing
    if (job.jobPostParsed === false && job.fullJobPost && job.fullJobPost.trim() !== '') {
      // console.log(`Parsing job structure for: ${job.title} (ID: ${job.id})`);
      
      try {
        const structuredData = await jobPostParser.parseJobPost(job.fullJobPost);
        
        if (structuredData) {
          await this.updateJobStructure(job.id, structuredData);
          
          updatedJob = {
            ...updatedJob,
            fullJobPost: structuredData.fullJobPost,
            aboutCompany: structuredData.aboutCompany,
            aboutProject: structuredData.aboutProject,
            aboutRole: structuredData.aboutRole,
            responsibilities: structuredData.responsibilities,
            requirements: structuredData.requirements,
            benefits: structuredData.benefits,
            searchKeywords: structuredData.searchKeywords, // ADD THIS LINE
            jobPostParsed: true
          };
          
          // console.log(`Successfully parsed and saved structure for job ${job.id}`);
        }
      } catch (error) {
        console.error(`Failed to parse job structure for ${job.id}:`, error);
      }
    }

    processedJobs.push(updatedJob);
  }
  
  return processedJobs;
}

/**
 * Process jobs (server-side): generate descriptions AND parse structure
 * @param {Array} jobs - Array of job objects
 * @returns {Promise<Array>} Jobs with generated descriptions and structured data
 */
async processJobServerSide(jobId) {
  try {
    const processJob = httpsCallable(functions, 'processJob');
    const result = await processJob({ jobId });
    return result.data;
  } catch (error) {
    console.error('Error calling processJob:', error);
    throw error;
  }
}

/**
 * Update only the searchKeywords field for a job
 * @param {string} jobId - Job document ID
 * @param {Array<string>} keywords - Array of keywords
 */
async updateJobKeywords(jobId, keywords) {
  try {
    const jobRef = doc(db, this.jobsCollection, jobId);
    await updateDoc(jobRef, {
      searchKeywords: keywords || []
    });
    // console.log(`✅ Updated keywords for job ${jobId}: ${keywords.length} keywords`);
  } catch (error) {
    console.error(`❌ Error updating keywords for job ${jobId}:`, error);
    throw error;
  }
}

/**
 * Parse and update job post structure
 * @param {string} jobId - Job document ID
 * @param {object} structuredData - The parsed job data
 */
async updateJobStructure(jobId, structuredData) {
  try {
    const jobRef = doc(db, this.jobsCollection, jobId);
    await updateDoc(jobRef, {
      fullJobPost: structuredData.fullJobPost || '',
      aboutCompany: structuredData.aboutCompany || '',
      aboutProject: structuredData.aboutProject || '',
      aboutRole: structuredData.aboutRole || '',
      responsibilities: structuredData.responsibilities || [],
      requirements: structuredData.requirements || [],
      benefits: structuredData.benefits || [],
      searchKeywords: structuredData.searchKeywords || [], // ADD THIS LINE
      jobPostParsed: true
    });
    // console.log(`Updated structure for job ${jobId}`);
  } catch (error) {
    console.error(`Error updating job structure ${jobId}:`, error);
    throw error;
  }
}

    /**
     * Reset pagination
     */
    resetPagination() {
        this.lastVisible = null;
    }

      /**
   * Delete a job and clean up associated keywords
   * @param {string} jobId - Job document ID to delete
   * @returns {object} - { success: boolean, error?: string }
   */
  async deleteJob(jobId) {
    try {
      const jobRef = doc(db, this.jobsCollection, jobId);
      const jobSnap = await getDoc(jobRef);
      
      if (!jobSnap.exists()) {
        return { success: false, error: 'Job not found' };
      }
      
      const jobData = jobSnap.data();
      const keywords = jobData.searchKeywords || [];
      
      // console.log(`Deleting job ${jobId} with ${keywords.length} keywords...`);
      
      // Delete the job first
      await deleteDoc(jobRef);
      // console.log('✅ Job deleted from Firestore');
      
      // Clean up keywords collection
      if (keywords.length > 0) {
        await keywordService.removeJobFromKeywords(jobId, keywords);
        // console.log('✅ Keywords cleaned up');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting job:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Batch delete multiple jobs
   * @param {Array<string>} jobIds - Array of job IDs to delete
   * @returns {object} - { successCount: number, failCount: number }
   */
  async batchDeleteJobs(jobIds) {
    let successCount = 0;
    let failCount = 0;
    
    for (const jobId of jobIds) {
      const result = await this.deleteJob(jobId);
      if (result.success) {
        successCount++;
      } else {
        failCount++;
      }
    }
    
    // console.log(`Batch delete complete: ${successCount} succeeded, ${failCount} failed`);
    return { successCount, failCount };
  }
}

export default new JobService();
